import { useToggle } from '@sparemin/blockhead';
import { useCallback, useEffect, useRef } from 'react';
import { AudioClipperComponent } from 'components/AudioClipper';
import { useClipSelectNavigation } from '../ClipSelectNavigationContext';
import { useClipSelectorPlaybackManager } from '../ClipSelector/ClipPlaybackManagerContext/ClipSelectorPlaybackManagerContext';

/**
 * Hook to manage playback of the various media assets in this component -
 * audio clipper, video clipper, and each suggested clip's video
 */
export default function usePlayback() {
  const [navState] = useClipSelectNavigation();

  const {
    currentlyPlayingClipId,
    stopAll: pauseClips,
  } = useClipSelectorPlaybackManager();

  const isSuggestionPlaying = currentlyPlayingClipId !== null;

  // although we have access to the videoClipperPlayer, playback state for that
  // component is managed reactively via passing in the isVideoPlaying prop.
  // the video clipper ref is only used for seeking
  const {
    toggleOn: playVideoClipper,
    toggleOff: pauseVideoClipper,
    value: isVideoClipperPlaying,
  } = useToggle(false);

  const audioClipperRef = useRef<AudioClipperComponent>();

  const pauseClipper = useCallback(() => {
    audioClipperRef.current?.pause();
    pauseVideoClipper();
  }, [pauseVideoClipper]);

  const pauseAll = useCallback(() => {
    pauseClipper();
    pauseClips();
  }, [pauseClipper, pauseClips]);

  useEffect(() => {
    if (!navState.matches('select')) {
      pauseClips();
    }

    if (!navState.matches('edit')) {
      pauseClipper();
    }
  }, [navState, pauseClipper, pauseClips]);

  return {
    audioClipperRef,
    isSuggestionPlaying,
    pauseAll,
    videoClipperPlayerControls: {
      playing: isVideoClipperPlaying,
      onPlay: playVideoClipper,
      onPause: pauseVideoClipper,
    },
  };
}
